import { createI18n } from 'vue-i18n';
import { nextTick } from 'vue';
import vuetify from '@/design-system/plugins/vuetify/vuetify';
import { LocaleEnum } from '@/types/enums/LocaleEnum';

export type MessageLanguages = `${LocaleEnum}`;

const SUPPORTED_LOCALES: string[] = Object.values(LocaleEnum);

export const isSupportedLocale = (locale: string | null): locale is MessageLanguages =>
    locale !== null && SUPPORTED_LOCALES.includes(locale);

export const getInitialLocale = (): MessageLanguages => {
    const savedLocale = localStorage.getItem('buylo-locale');

    return isSupportedLocale(savedLocale) ? savedLocale : 'cs';
};

const i18n = createI18n({
    legacy: false,
    locale: getInitialLocale(),
    fallbackLocale: 'en',
    messages: {},
});

// Content hash of the BE-generated locale files. Used to invalidate the localStorage
// cache the moment translations change on the backend (see /be/locales/version).
async function fetchLocaleVersion(): Promise<string | null> {
    try {
        const response = await fetch('/be/locales/version', { cache: 'no-store' });
        if (!response.ok) {
            return null;
        }
        const { version } = await response.json();

        return typeof version === 'string' ? version : null;
    } catch {
        return null;
    }
}

export async function loadLocaleMessages(locale: MessageLanguages, force = false): Promise<void> {
    if (!force && i18n.global.availableLocales.includes(locale)) {
        return;
    }

    const cacheKey = `buylo-locale-${locale}`;

    if (force) {
        localStorage.removeItem(cacheKey);
    }

    const currentVersion = await fetchLocaleVersion();

    try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            try {
                const { messages, timestamp, version } = JSON.parse(cached);

                // Prefer the backend version hash; fall back to a 1-hour TTL when the
                // version endpoint is unreachable so translations still refresh eventually.
                const cacheIsFresh =
                    currentVersion !== null ? version === currentVersion : Date.now() - timestamp < 3600000;

                if (cacheIsFresh) {
                    i18n.global.setLocaleMessage(locale, messages);

                    return;
                }
            } catch {
                localStorage.removeItem(cacheKey);
            }
        }

        const response = await fetch(`/be/locales/${locale}.json`);
        if (!response.ok) {
            throw new Error(`Failed to load locale ${locale}: ${response.status}`);
        }
        const messages = await response.json();
        i18n.global.setLocaleMessage(locale, messages);
        localStorage.setItem(
            cacheKey,
            JSON.stringify({
                messages,
                timestamp: Date.now(),
                version: currentVersion,
            }),
        );
    } catch (error) {
        console.error(`Failed to load locale ${locale}:`, error);
        throw error;
    }
}

export async function setI18nLanguage(locale: MessageLanguages, force = false): Promise<void> {
    await loadLocaleMessages(locale, force);
    i18n.global.locale.value = locale;
    vuetify.locale.current.value = locale;
    document.querySelector('html')?.setAttribute('lang', locale);
    localStorage.setItem('buylo-locale', locale);
    await nextTick();
}

export default i18n;
